/// <amd-module name="Agile/Taskboard/CustomColumn/Components/CustomColumnPanel" />
import * as Resources from "Agile/Taskboard/CustomColumn/Resources";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import {
    BoltListDragEvent,
    IListDropData,
    IListItemDetails,
    ListDragDropBehavior,
    ListDragImage,
    ListItem,
    ListSelection,
    ScrollableList
} from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { SingleLayerMasterPanel } from "VSSUI/MasterDetails";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Panel } from "VSSUI/Panel";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { DragAndDropGripper } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ArrayItemProvider, IItemProvider } from "VSSUI/Utilities/Provider";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work";
import { CustomColumnActions } from "../State/CustomColumnActions";
import {
    ColumnToStateMap,
    IColumn,
    ICustomColumnState,
    IWorkItemTypeStateStatus,
    MAX_COLUMN_LIMIT,
    WorkItemStateCategory
} from "../State/CustomColumnContracts";
import { customColumnPanelReducer } from "../State/CustomColumnPanelReducer";

export type KeyNameMap = { [key: string]: string /** name */ };
export interface ICustomColumnPanelProps {
    context?: IVssComponentContext;
    teamId: string;
    columns: IColumn[];
    normalizedTaskWorkItemTypesToStateMap: { [workItemTypeNameKey: string]: { [stateNameKey: string]: WorkItemStateCategory } };
    normalizedColumnIdToWorkItemTypeStateMap: { [columnId: string]: { [workItemTypeNameKey: string]: string } };
    stateKeyNameMap: KeyNameMap;
    witKeyNameMap: KeyNameMap;
    onClose: () => void;
}

const CustomColumnPanelInternal: React.FunctionComponent<ICustomColumnPanelProps> = props => {
    const [state, dispatch] = React.useReducer(customColumnPanelReducer, {
        columns: props.columns,
        normalizedTaskWorkItemTypesToStateMap: props.normalizedTaskWorkItemTypesToStateMap,
        normalizedColumnIdToWorkItemTypeStateMap: transformColumnIdToStateMap(props.normalizedColumnIdToWorkItemTypeStateMap),
        selectedColumnIndex: 0,
        isDirty: false,
        isValid: true,
        unmappedStateKeys: {},
        stateKeyNameMap: props.stateKeyNameMap,
        witKeyNameMap: props.witKeyNameMap,
        addNewColumnFailed: false
    });

    const { selectedColumnIndex } = state;

    // Do initial validation of the state
    React.useEffect(() => {
        dispatch(CustomColumnActions.validate());
    }, []);
    const itemProvider = new ArrayItemProvider<IColumn>(state.columns);

    // Define callback to save column settings
    const onSaveCallback = React.useCallback(async () => {
        dispatch(CustomColumnActions.saving());
        try {
            const pageService = props.context!.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageData = pageService.getData();
            if (!pageData || !pageData.project) {
                throw new Error("Project missing");
            }
            await saveColumnSettings(pageData.project.id, props.teamId, props.context!.pageContext, state);
            // force reload the page once the save is successful
            window.location.reload();
        } catch (error) {
            dispatch(CustomColumnActions.saveFailed(error));
        }
    }, [state]);

    const onColumnChangedCallback = React.useCallback((index: number) => {
        dispatch(CustomColumnActions.selectColumn(index));
    }, []);

    const onAddColumnCallback = React.useCallback(() => {
        dispatch(CustomColumnActions.addColumn());
    }, []);

    const onRenameColumnCallback = React.useCallback((columnId: string, newColumnName: string) => {
        dispatch(CustomColumnActions.renameColumn(columnId, newColumnName));
    }, []);

    const onStateMappedCallback = React.useCallback((columnId: string, workItemType: string, stateName: string) => {
        dispatch(CustomColumnActions.updateState(columnId, workItemType, stateName));
    }, []);

    const onRemoveCallback = React.useCallback((columnId: string) => {
        dispatch(CustomColumnActions.removeColumn(columnId));
    }, []);

    const onReorderCallback = React.useCallback((columnId: string, newIndex: number) => {
        dispatch(CustomColumnActions.reorderColumn(columnId, newIndex));
    }, []);

    const saveError = state.savingError && (
        <MessageCard className="custom-column-error-message" severity={MessageCardSeverity.Error}>
            {state.savingError.message}
        </MessageCard>
    );
    const addNewColumnError = state.addNewColumnFailed && (
        <MessageCard className="custom-column-error-message" severity={MessageCardSeverity.Error}>
            {localeFormat(Resources.ValidationError_ColumnLimit, MAX_COLUMN_LIMIT)}
        </MessageCard>
    );

    return (
        <Panel
            onDismiss={() => props.onClose()}
            size={ContentSize.Auto}
            titleProps={{ text: Resources.PanelTitle }}
            description={Resources.PanelSubTitle}
            footerButtonProps={[
                { text: Resources.Cancel, onClick: () => props.onClose() },
                { text: Resources.Save, primary: true, onClick: onSaveCallback, disabled: !state.isDirty || !state.isValid }
            ]}
        >
            <div className="flex-column flex-grow custom-column-panel">
                {saveError}
                {addNewColumnError}
                {renderUnmappedError(state.unmappedStateKeys, state.stateKeyNameMap, state.witKeyNameMap)}
                <div className="flex-row column-list-mapping-form">
                    <SingleLayerMasterPanel
                        renderContent={() =>
                            renderLeftPanel(
                                itemProvider,
                                selectedColumnIndex,
                                state.addNewColumnFailed,
                                onColumnChangedCallback,
                                onAddColumnCallback,
                                onRemoveCallback,
                                onReorderCallback
                            )
                        }
                    />
                    {renderColumnMappingForm(state, onStateMappedCallback, onRenameColumnCallback)}
                </div>
            </div>
        </Panel>
    );
};
const renderUnmappedError = (
    unmappedStateKeys: { [workItemTypeNameKey: string]: string[] },
    stateKeyNameMap: KeyNameMap,
    witKeyNameMap: KeyNameMap
) => {
    if (unmappedStateKeys) {
        const workitemTypes = Object.keys(unmappedStateKeys).sort(localeIgnoreCaseComparer);
        return (
            <div>
                {workitemTypes.map(workitemType => {
                    const states = unmappedStateKeys[workitemType].map(state => stateKeyNameMap[state]).join(", ");
                    return (
                        <MessageCard key={workitemType} className="custom-column-error-message" severity={MessageCardSeverity.Error}>
                            {localeFormat(Resources.ValidationError_UnmappedState, states, witKeyNameMap[workitemType])}
                        </MessageCard>
                    );
                })}
            </div>
        );
    }
};

const renderLeftPanel = (
    itemProvider: IItemProvider<IColumn>,
    selectedColumnIndex: number,
    disableAddColumn: boolean,
    onColumnChangedCallback: (index: number) => void,
    onAddColumn: () => void,
    onRemoveColumn: (columnId: string) => void,
    onReorder: (columnId: string, newIndex: number) => void
) => {
    const selection = React.useRef(
        new ListSelection({
            multiSelect: false,
            selectOnFocus: false
        })
    ).current;

    const listKey = React.useRef({ value: 0 }).current;

    if (selection.selectedCount === 0 || selection.value[0].beginIndex !== selectedColumnIndex) {
        selection.select(selectedColumnIndex);
    }

    React.useEffect(() => {
        const callback = (range: ISelectionRange[]) => {
            onColumnChangedCallback(range[0].beginIndex);
        };
        selection.subscribe(callback);
        return () => {
            selection.unsubscribe(callback);
        };
    });

    const onDragOver = (event: BoltListDragEvent<HTMLElement, IColumn>, dropData: IListDropData) => {
        // disable reorder to first and last column
        if (dropData.index <= 0 || dropData.index >= itemProvider.length) {
            event.detail.dataTransfer.dropEffect = DragDropEffect.none;
        }
    };

    const onDragStart = (event: BoltListDragEvent<HTMLElement, IColumn>) => {
        // disable reorder for first and last column
        const order = event.detail.dataTransfer.data.order;
        if (order === 0 || order === itemProvider.length - 1) {
            event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
        }
    };

    const onDrop = (event: BoltListDragEvent<HTMLElement, IColumn>, dropData: IListDropData) => {
        const column = event.detail.dataTransfer.data;
        onReorder(column.id, dropData.index);
    };

    const dragDropBehavior = new ListDragDropBehavior<IColumn>({
        allowedTypes: ["taskboard-column-item"],
        id: "taskboard-column-list",
        onDragOver: onDragOver,
        onDragStart: onDragStart,
        onDrop: onDrop,
        renderDragImage: (event: BoltListDragEvent<HTMLElement, IColumn>) => <ListDragImage text={event.detail.dataTransfer.data.name} />,
        type: "taskboard-column-item"
    });

    const renderColumnItem = (index: number, item: IColumn, details: IListItemDetails<IColumn>, key?: string): JSX.Element => {
        const content = !(item.error || item.stateMappingError) ? (
            item.name
        ) : (
            <div className="flex">
                <Status {...Statuses.Warning} key="warning" size={StatusSize.m} className="flex-self-center custom-column-panel-error-icon" />
                {item.name}
            </div>
        );
        const isNotFirstOrLastColumn = index > 0 && index < itemProvider.length - 1;
        return (
            <ListItem
                className={css(
                    "custom-column-panel-row",
                    isNotFirstOrLastColumn && "custom-column-panel-row--draggable",
                    isNotFirstOrLastColumn && "custom-column-panel-row--removeable"
                )}
                key={key || "list-item" + index}
                index={index}
                details={details}
            >
                <div className="custom-column-panel-row-content flex-row flex-center flex-grow h-scroll-hidden">
                    <DragAndDropGripper />
                    <div className="primary-text text-ellipsis column-name flex-grow">{content}</div>
                    <Button
                        ariaLabel={Resources.RemoveButton}
                        className="remove-button"
                        subtle={true}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => onRemoveColumn(item.id)}
                    />
                </div>
            </ListItem>
        );
    };

    return (
        <div>
            <Button
                className="add-button"
                primary={true}
                text={Resources.AddColumn}
                iconProps={{ iconName: "Add" }}
                onClick={onAddColumn}
                disabled={disableAddColumn}
            />
            <ScrollableList
                key={listKey.value++} // unique key to force re-render with updated itemProvider
                itemProvider={itemProvider}
                renderRow={renderColumnItem}
                width="100%"
                selection={selection}
                singleClickActivation={true}
                behaviors={[dragDropBehavior]}
            />
        </div>
    );
};

const renderColumnMappingForm = (
    state: ICustomColumnState,
    onStateMappedCallback: (columnId: string, workItemType: string, stateName: string) => void,
    onRenameColumnCallback: (columnId: string, newColumnName: string) => void
) => {
    const {
        columns,
        selectedColumnIndex,
        normalizedColumnIdToWorkItemTypeStateMap: columnIdToWorkItemTypeStateMap,
        normalizedTaskWorkItemTypesToStateMap: taskWorkItemsToStateMap,
        stateKeyNameMap: stateMap,
        witKeyNameMap: workItemTypeMap
    } = state;
    const selectedColumn = columns[selectedColumnIndex];
    const workItemTypeToStateMap = columnIdToWorkItemTypeStateMap[selectedColumn.id];
    const workItemTypeKeys = Object.keys(workItemTypeToStateMap).sort(localeIgnoreCaseComparer);
    const allowedStateCategories =
        selectedColumnIndex === 0
            ? [WorkItemStateCategory.Proposed, WorkItemStateCategory.InProgress]
            : selectedColumnIndex === columns.length - 1
            ? [WorkItemStateCategory.Completed]
            : undefined;
    return (
        <Page className="flex-grow custom-column-mapping-form">
            <div>
                <Header
                    title={localeFormat(Resources.StateMappingSectionTitle, selectedColumn.name)}
                    titleSize={TitleSize.Small}
                    description={Resources.StateMappingSectionDescription}
                />
            </div>
            <table className="mapping-table" key={selectedColumn.id}>
                <tbody>
                    {columnRenameSection(selectedColumn, onRenameColumnCallback)}
                    {workItemTypeKeys.map(workItemTypeKey => {
                        const stateSelection = workItemTypeToStateMap[workItemTypeKey];
                        const statesMap = taskWorkItemsToStateMap[workItemTypeKey];
                        return (
                            <WorkItemStateSelection
                                key={`${selectedColumn.id}-${workItemTypeKey}`}
                                columnId={selectedColumn.id}
                                workItemTypeKey={workItemTypeKey}
                                states={statesMap}
                                stateNameMap={stateMap}
                                workItemTypeMap={workItemTypeMap}
                                stateSelection={stateSelection}
                                onStateMappedCallback={onStateMappedCallback}
                                allowedStateCategories={allowedStateCategories}
                            />
                        );
                    })}
                </tbody>
            </table>
        </Page>
    );
};

const WorkItemStateSelection = (props: {
    columnId: string;
    workItemTypeKey: string;
    states: { [stateNameKey: string]: WorkItemStateCategory };
    workItemTypeMap: { [workItemTypeNameKey: string]: string };
    stateNameMap: { [stateNameKey: string]: string };
    stateSelection: IWorkItemTypeStateStatus;
    onStateMappedCallback: (columnId: string, workItemType: string, stateName: string) => void;
    allowedStateCategories?: WorkItemStateCategory[];
}) => {
    const { states, workItemTypeKey, columnId, stateSelection, onStateMappedCallback, allowedStateCategories } = props;
    const stateNames = Object.keys(states);
    const stateItems = stateNames
        .filter(stateNameKey => !allowedStateCategories || allowedStateCategories.findIndex(cat => cat === states[stateNameKey]) >= 0)
        .map(stateNameKey => {
            return {
                id: stateNameKey,
                text: props.stateNameMap[stateNameKey]
            };
        });
    const selectedIndex = stateItems.findIndex(stateItem => localeIgnoreCaseComparer(stateItem.text, stateSelection.stateNameKey) === 0);
    const selection = React.useRef(new ListSelection({ selectOnFocus: false })).current;
    if (selectedIndex >= 0) {
        selection.select(selectedIndex);
    } else {
        // Previously stored value is not available anymore.
        stateItems.unshift({
            id: stateSelection.stateNameKey,
            text: props.stateNameMap[stateSelection.stateNameKey] || stateSelection.stateNameKey
        });
        selection.select(0);
    }

    const onSelectCallback = React.useCallback(
        (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<any>) => {
            onStateMappedCallback(columnId, workItemTypeKey, item.text!);
        },
        [columnId, workItemTypeKey]
    );

    return (
        <tr className="custom-column-mapping-row">
            <td className="work-item-type-row-title">{props.workItemTypeMap[workItemTypeKey]}</td>
            <td className="work-item-type-row-value">
                <FormItem message={stateSelection.error || ""} error={!!stateSelection.error}>
                    <Dropdown
                        ariaLabel={props.workItemTypeMap[workItemTypeKey]}
                        placeholder={Resources.None}
                        items={stateItems}
                        selection={selection}
                        onSelect={onSelectCallback}
                    />
                </FormItem>
            </td>
        </tr>
    );
};

const columnRenameSection = (column: IColumn, onRenameColumnCallback: (columnId: string, newColumnName: string) => void) => {
    const renameInput = React.useRef<TextField>(null);
    React.useEffect(() => {
        if (renameInput.current) {
            renameInput.current.focus();
        }
    }, [column]);
    return (
        <tr className="custom-column-mapping-row">
            <td className="work-item-type-row-title">{Resources.ColumnName}</td>
            <td className="work-item-type-row-value">
                <FormItem message={column.error || ""} error={!!column.error}>
                    <TextField
                        ariaLabel={Resources.ColumnName}
                        ref={renameInput}
                        maxLength={129}
                        value={column.name}
                        onChange={(e, newValue) => onRenameColumnCallback(column.id, newValue)}
                    />
                </FormItem>
            </td>
        </tr>
    );
};

function transformColumnIdToStateMap(propsMap: { [columnId: string]: { [workItemTypeName: string]: string } }): ColumnToStateMap {
    const result: ColumnToStateMap = {};
    for (const columnId in propsMap) {
        result[columnId] = {};
        for (const workItemTypeName in propsMap[columnId]) {
            const stateName = propsMap[columnId][workItemTypeName];
            result[columnId][workItemTypeName] = { stateNameKey: stateName };
        }
    }

    return result;
}

async function saveColumnSettings(projectId: string, teamId: string, pageContext: IVssPageContext, state: ICustomColumnState) {
    const workRestClient = pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
    const { columns, witKeyNameMap, stateKeyNameMap, normalizedColumnIdToWorkItemTypeStateMap } = state;
    const updateTaskBoardColumns = columns.map(col => {
        const { id, isNew, name, order } = col;
        const workItemTypeToStateMap = normalizedColumnIdToWorkItemTypeStateMap[id];
        const mappings = Object.keys(workItemTypeToStateMap).map(workItemTypeKey => {
            return {
                workItemType: witKeyNameMap[workItemTypeKey],
                state: stateKeyNameMap[workItemTypeToStateMap[workItemTypeKey].stateNameKey]
            };
        });

        return {
            id: isNew ? "" : id,
            name,
            order,
            mappings
        };
    });
    return workRestClient.updateColumns(updateTaskBoardColumns, { projectId, teamId, project: projectId, team: teamId });
}

export class CustomColumnPanel extends VssComponent<ICustomColumnPanelProps> {
    constructor(props: ICustomColumnPanelProps) {
        super(props);
    }
    public render() {
        return <CustomColumnPanelInternal context={this.context} {...this.props} />;
    }
}

VssComponent.register("taskboard-custom-column-panel", CustomColumnPanel);
